/* eslint-disable unicorn/prefer-module */
import { useCallback, useEffect, useMemo, useState } from "react";
import { FaInfoCircle } from "react-icons/fa";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { Loader } from "..";
import { getEdificiosOperativosPublico, useCategoriaMoneda } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaTransporte.module.scss";

// =====================================================
// DATOS IPSOS TRANSPORTE (Base: 400 casos)
// =====================================================
const IPSOS_TRANSPORTE = {
  // Medio de transporte para llegar al trabajo
  medioTransporte: {
    "Locomoción colectiva": 0.519,
    "Vehículo particular": 0.211,
    Metro: 0.164,
    Caminando: 0.042,
    "Taxi / Aplicaciones": 0.027,
    Scooter: 0.02,
    Micro: 0.009,
    Bicicleta: 0.005,
    "Vehículo empresa": 0.003,
  },
  // Qué valora más en un medio de transporte
  valoracion: {
    Rapidez: 0.724,
    Comodidad: 0.219,
    Costo: 0.05,
    Seguridad: 0.007,
  },
  // Ha usado aplicaciones de transporte
  usaApps: 0.891, // 89.1% sí usa
  noUsaApps: 0.109, // 10.9% no usa
  // Apps instaladas en el teléfono
  appsInstaladas: {
    Uber: 0.835,
    Cabify: 0.251,
    Didi: 0.168,
    Ninguna: 0.094,
    Otra: 0.017,
    Tranvip: 0.017,
  },
  // App que prefiere o más utiliza
  appPreferida: {
    Uber: 0.798,
    Cabify: 0.069,
    Didi: 0.051,
    Ninguna: 0.083,
  },
};

// Datos de precios promedio por viaje (CLP)
const PRECIOS_VIAJE = {
  Uber: { corto: 3500, medio: 6500, largo: 12000 },
  Cabify: { corto: 3800, medio: 7000, largo: 13000 },
  Didi: { corto: 3200, medio: 6000, largo: 11000 },
};

// Frecuencia de uso estimada (viajes por mes)
const FRECUENCIA_USO = {
  intensivo: { label: "Intensivo (diario)", viajesMes: 40, pct: 0.15 },
  frecuente: { label: "Frecuente (3-4x semana)", viajesMes: 14, pct: 0.25 },
  moderado: { label: "Moderado (1-2x semana)", viajesMes: 6, pct: 0.35 },
  ocasional: { label: "Ocasional (1-2x mes)", viajesMes: 1.5, pct: 0.25 },
};

// Escenarios de captación
const ESCENARIOS_CAPTACION = [2, 4, 6, 8, 10];

export const Transporte = () => {
  const { formatNumber, formatLocal, convertCLP, pais } = useCategoriaMoneda();
  const [edificios, setEdificios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkAll, setCheckAll] = useState(true);
  const [checkedBuildings, setCheckedBuildings] = useState({});
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [verAnual, setVerAnual] = useState(false);
  const [showOportunidadModal, setShowOportunidadModal] = useState(false);

  // Estado para simulador
  const [appSeleccionada, setAppSeleccionada] = useState("Uber");
  const [tipoViaje, setTipoViaje] = useState("medio");
  const [porcentajeCaptacion, setPorcentajeCaptacion] = useState(4);

  useEffect(() => {
    const initialize = async () => {
      try {
        const newEdificios = await getEdificiosOperativosPublico({ pais });
        setEdificios(newEdificios);

        const initialChecked = {};
        newEdificios.forEach(({ id }) => {
          initialChecked[id] = true;
        });
        setCheckedBuildings(initialChecked);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [pais]);

  const toggleCheckAll = useCallback(() => {
    if (!checkAll) {
      const allChecked = {};
      edificios.forEach(({ id }) => {
        allChecked[id] = true;
      });
      setCheckedBuildings(allChecked);
    } else {
      setCheckedBuildings({});
    }
    setCheckAll((prev) => !prev);
  }, [checkAll, edificios]);

  const toggleCheck = useCallback((id) => {
    setCheckedBuildings((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  // Calcular totales de edificios seleccionados
  const { totalEjecutivos, cantidadEdificios, totalPantallas, totalEmpresas } = useMemo(() => {
    let ejecutivos = 0;
    let cantidad = 0;
    let pantallas = 0;
    let empresas = 0;

    edificios.forEach(({ id, personas, pantallas: p, espera, totems, cowork, empresaslistado }) => {
      if (checkedBuildings[id]) {
        cantidad++;
        ejecutivos += personas || 0;
        pantallas += (p || 0) + (espera || 0) + (totems || 0) + (cowork || 0);
        empresas += empresaslistado ? empresaslistado.split(",").length : 0;
      }
    });

    return {
      totalEjecutivos: ejecutivos,
      cantidadEdificios: cantidad,
      totalPantallas: pantallas,
      totalEmpresas: empresas,
    };
  }, [edificios, checkedBuildings]);

  // =====================================================
  // CÁLCULOS DE APPS DE TRANSPORTE
  // =====================================================
  const resultados = useMemo(() => {
    if (!totalEjecutivos) return null;

    const usanApps = Math.round(totalEjecutivos * IPSOS_TRANSPORTE.usaApps);
    const noUsanApps = Math.round(totalEjecutivos * IPSOS_TRANSPORTE.noUsaApps);

    // Usuarios por app instalada
    const usuariosPorApp = {};
    Object.entries(IPSOS_TRANSPORTE.appsInstaladas).forEach(([app, pct]) => {
      usuariosPorApp[app] = Math.round(usanApps * pct);
    });

    // Preferencia de app
    const preferenciaPorApp = {};
    Object.entries(IPSOS_TRANSPORTE.appPreferida).forEach(([app, pct]) => {
      preferenciaPorApp[app] = Math.round(usanApps * pct);
    });

    // Calcular viajes mensuales por frecuencia de uso
    let totalViajesMes = 0;
    const desgloseFrecuencia = {};

    Object.entries(FRECUENCIA_USO).forEach(([key, data]) => {
      const usuarios = Math.round(usanApps * data.pct);
      const viajes = usuarios * data.viajesMes;
      desgloseFrecuencia[key] = {
        label: data.label,
        usuarios,
        viajesMes: Math.round(viajes),
      };
      totalViajesMes += viajes;
    });

    // Calcular mercado por app (usando precio medio como referencia)
    const mercadoPorApp = {};
    Object.entries(IPSOS_TRANSPORTE.appPreferida).forEach(([app, pct]) => {
      if (app === "Ninguna") return;
      const viajesApp = totalViajesMes * pct;
      const precioMedio = PRECIOS_VIAJE[app]?.medio || 6500;
      mercadoPorApp[app] = {
        share: pct,
        viajesMes: Math.round(viajesApp),
        mercadoMensual: Math.round(viajesApp * precioMedio),
      };
    });

    // Mercado total
    const mercadoTotalMensual = Object.values(mercadoPorApp).reduce((sum, app) => sum + app.mercadoMensual, 0);

    return {
      usanApps,
      noUsanApps,
      usuariosPorApp,
      preferenciaPorApp,
      desgloseFrecuencia,
      totalViajesMes: Math.round(totalViajesMes),
      totalViajesAnual: Math.round(totalViajesMes * 12),
      mercadoPorApp,
      mercadoTotalMensual,
      mercadoTotalAnual: mercadoTotalMensual * 12,
    };
  }, [totalEjecutivos]);

  // =====================================================
  // CÁLCULO OPORTUNIDAD
  // =====================================================
  const calcularOportunidad = useCallback(() => {
    if (!resultados) return null;

    const datosApp = resultados.mercadoPorApp[appSeleccionada];
    if (!datosApp) return null;

    const precioViaje = PRECIOS_VIAJE[appSeleccionada][tipoViaje];
    const mercadoCaptadoMensual = Math.round(resultados.mercadoTotalMensual * (porcentajeCaptacion / 100));
    const viajesCaptados = Math.round(mercadoCaptadoMensual / precioViaje);

    return {
      mercadoTotal: resultados.mercadoTotalMensual,
      mercadoCaptadoMensual,
      mercadoCaptadoAnual: mercadoCaptadoMensual * 12,
      viajesCaptadosMes: viajesCaptados,
      precioViaje,
    };
  }, [resultados, appSeleccionada, tipoViaje, porcentajeCaptacion]);

  if (loading || edificios.length === 0) return <Loader />;

  return (
    <div className={styles.transportePage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>APPS DE TRANSPORTE</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <div className={styles.transporteContent}>
        {/* Resumen de penetración */}
        {resultados && (
          <div className={styles.resumenPenetracion}>
            <div className={styles.resumenCard}>
              <div className={styles.resumenValor}>
                <span className={styles.badgeIpsos}>{(IPSOS_TRANSPORTE.usaApps * 100).toFixed(0)}%</span>
              </div>
              <div className={styles.resumenLabel}>Usa apps de transporte</div>
              <div className={styles.resumenNumero}>{formatNumber(resultados.usanApps)} personas</div>
            </div>
            <div className={styles.resumenCard}>
              <div className={styles.resumenValor}>
                <span className={styles.valorDestacado}>{formatNumber(resultados.totalViajesMes)}</span>
                <span className={styles.tooltipContainer}>
                  <FaInfoCircle className={styles.infoIcon} />
                  <span className={styles.tooltipText}>
                    Estimación basada en frecuencia de uso: 15% intensivo, 25% frecuente, 35% moderado, 25% ocasional.
                  </span>
                </span>
              </div>
              <div className={styles.resumenLabel}>Viajes estimados / mes</div>
              <div className={styles.resumenDetalle}>{formatNumber(resultados.totalViajesAnual)} viajes/año</div>
            </div>
            <div className={styles.resumenCard}>
              <div className={styles.resumenValor}>
                <span className={styles.valorDestacado}>{formatLocal(convertCLP(resultados.mercadoTotalMensual))}</span>
              </div>
              <div className={styles.resumenLabel}>Mercado mensual</div>
              <div className={styles.resumenDetalle}>{formatLocal(convertCLP(resultados.mercadoTotalAnual))}/año</div>
            </div>
          </div>
        )}

        {/* Sección Apps Instaladas y Preferencia */}
        {resultados && (
          <div className={styles.appsSection}>
            {/* Apps Instaladas */}
            <div className={styles.appsCard}>
              <h4 className={styles.subtitulo}>Apps Instaladas</h4>
              <div className={styles.appsList}>
                {Object.entries(IPSOS_TRANSPORTE.appsInstaladas)
                  .filter(([app]) => app !== "Ninguna" && app !== "Otra" && app !== "Tranvip")
                  .map(([app, pct]) => (
                    <div key={app} className={styles.appItem}>
                      <div className={styles.appNombre}>{app}</div>
                      <div className={styles.appBar}>
                        <div className={styles.appBarFill} style={{ width: `${pct * 100}%` }} />
                      </div>
                      <div className={styles.appStats}>
                        <span className={styles.badgeIpsos}>{(pct * 100).toFixed(0)}%</span>
                        <span className={styles.appUsuarios}>{formatNumber(resultados.usuariosPorApp[app])}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* App Preferida */}
            <div className={styles.appsCard}>
              <h4 className={styles.subtitulo}>App Preferida / Más Usada</h4>
              <div className={styles.appsList}>
                {Object.entries(IPSOS_TRANSPORTE.appPreferida)
                  .filter(([app]) => app !== "Ninguna")
                  .map(([app, pct]) => (
                    <div key={app} className={styles.appItem}>
                      <div className={styles.appNombre}>{app}</div>
                      <div className={styles.appBar}>
                        <div
                          className={cx(styles.appBarFill, styles.appBarPreferida)}
                          style={{ width: `${pct * 100}%` }}
                        />
                      </div>
                      <div className={styles.appStats}>
                        <span className={styles.badgeIpsos}>{(pct * 100).toFixed(0)}%</span>
                        <span className={styles.appUsuarios}>{formatNumber(resultados.preferenciaPorApp[app])}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* Tabla de frecuencia de uso */}
        {resultados && (
          <div className={styles.tablaContainer}>
            <h4 className={styles.subtitulo}>Frecuencia de Uso Estimada</h4>
            <table className={cx("table table-sm", styles.tablaResultados)}>
              <thead className="table-dark">
                <tr>
                  <th className={cx("text-center", styles.thNoUsa)}>No usan apps</th>
                  <th className={cx("text-center", styles.thSiUsa)}>Sí usan apps</th>
                  <th className="text-center">Frecuencia</th>
                  <th className="text-center">Usuarios</th>
                  <th className="text-center">
                    <div className={styles.headerConsumos}>
                      <span>Viajes</span>
                      <div className={styles.toggleContainer}>
                        <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                        <label className={styles.toggleSwitch}>
                          <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                          <span className={styles.toggleSlider} />
                        </label>
                        <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(resultados.desgloseFrecuencia).map(([key, data], idx) => (
                  <tr key={key}>
                    {idx === 0 && (
                      <>
                        <td rowSpan={4} className={cx("text-center align-middle", styles.celdaNo)}>
                          <div className={styles.celdaContenido}>
                            <span className={styles.badgeIpsos}>{(IPSOS_TRANSPORTE.noUsaApps * 100).toFixed(0)}%</span>
                            <div className={styles.valorGrande}>{formatNumber(resultados.noUsanApps)}</div>
                          </div>
                        </td>
                        <td rowSpan={4} className={cx("text-center align-middle", styles.celdaSi)}>
                          <div className={styles.celdaContenido}>
                            <span className={styles.badgeIpsos}>{(IPSOS_TRANSPORTE.usaApps * 100).toFixed(0)}%</span>
                            <div className={styles.valorGrande}>{formatNumber(resultados.usanApps)}</div>
                          </div>
                        </td>
                      </>
                    )}
                    <td className={styles.celdaFrecuencia}>
                      <span className={styles.frecuenciaTexto}>{data.label}</span>
                    </td>
                    <td className="text-center">{formatNumber(data.usuarios)}</td>
                    <td className="text-center">
                      <strong>{formatNumber(verAnual ? data.viajesMes * 12 : data.viajesMes)}</strong>
                    </td>
                  </tr>
                ))}
                <tr className={styles.filaTotales}>
                  <td colSpan={4} className={styles.celdaVacia} />
                  <td className={cx("text-center", styles.celdaTotal)}>
                    <strong>{formatNumber(verAnual ? resultados.totalViajesAnual : resultados.totalViajesMes)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Market Share por App */}
        {resultados && (
          <div className={styles.marketShareContainer}>
            <h4 className={styles.marketShareTitulo}>Market Share - Apps de Transporte</h4>
            <div className={styles.marketShareGrid}>
              {Object.entries(resultados.mercadoPorApp).map(([app, data]) => (
                <div key={app} className={styles.marketShareCard}>
                  <div className={styles.cardNombre}>{app}</div>
                  <div className={styles.cardShare}>
                    <span className={styles.badgeIpsos}>{(data.share * 100).toFixed(0)}%</span>
                  </div>
                  <div className={styles.cardMetricas}>
                    <div>
                      <span className={styles.metricaLabel}>Viajes/{verAnual ? "año" : "mes"}</span>
                      <span className={styles.metricaValor}>
                        {formatNumber(verAnual ? data.viajesMes * 12 : data.viajesMes)}
                      </span>
                    </div>
                    <div>
                      <span className={styles.metricaLabel}>Mercado/{verAnual ? "año" : "mes"}</span>
                      <span className={styles.metricaValor}>
                        {formatLocal(convertCLP(verAnual ? data.mercadoMensual * 12 : data.mercadoMensual))}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
              Simular Oportunidad
            </button>
          </div>
        )}

        {/* Qué valoran más */}
        <div className={styles.valoracionContainer}>
          <h4 className={styles.subtitulo}>Lo que más valoran en transporte</h4>
          <div className={styles.valoracionGrid}>
            {Object.entries(IPSOS_TRANSPORTE.valoracion).map(([item, pct]) => (
              <div key={item} className={styles.valoracionItem}>
                <span className={styles.valoracionNombre}>{item}</span>
                <span className={styles.badgeIpsos}>{(pct * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div className={styles.disclaimer}>
          <p>
            <strong>Fuente:</strong> IPSOS Wecast 2025. Base: 400 casos. El 89.1% ha usado apps de transporte. Uber
            lidera con 83.5% de instalación y 79.8% de preferencia. Frecuencia de uso y ticket promedio estimados según
            estudios del mercado chileno.
          </p>
        </div>
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> ejecutivos
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Pantallas</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(({ id, edificio, ubicacion, personas, pantallas, espera, totems, cowork }) => (
                    <tr
                      key={id}
                      onClick={() => toggleCheck(id)}
                      className={cx(checkedBuildings[id] && "table-success")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                      </td>
                      <td className="text-center">{edificio.toUpperCase()}</td>
                      <td className="text-center">{ubicacion}</td>
                      <td className="text-center">
                        {formatNumber((pantallas || 0) + (espera || 0) + (totems || 0) + (cowork || 0))}
                      </td>
                      <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Oportunidad */}
      {showOportunidadModal && resultados && (
        <div className={styles.modalOverlay} onClick={() => setShowOportunidadModal(false)}>
          <div className={cx(styles.modalContent, styles.modalOportunidad)} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Simulador de Oportunidad - Apps de Transporte</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowOportunidadModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.simuladorContainer}>
                <div className={styles.simuladorControles}>
                  <div className={styles.controlGroup}>
                    <label>App objetivo:</label>
                    <select
                      value={appSeleccionada}
                      onChange={(e) => setAppSeleccionada(e.target.value)}
                      className="form-select form-select-sm"
                    >
                      {Object.keys(PRECIOS_VIAJE).map((app) => (
                        <option key={app} value={app}>
                          {app}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.controlGroup}>
                    <label>Tipo de viaje:</label>
                    <select
                      value={tipoViaje}
                      onChange={(e) => setTipoViaje(e.target.value)}
                      className="form-select form-select-sm"
                    >
                      <option value="corto">
                        Corto (~{formatLocal(convertCLP(PRECIOS_VIAJE[appSeleccionada].corto))})
                      </option>
                      <option value="medio">
                        Medio (~{formatLocal(convertCLP(PRECIOS_VIAJE[appSeleccionada].medio))})
                      </option>
                      <option value="largo">
                        Largo (~{formatLocal(convertCLP(PRECIOS_VIAJE[appSeleccionada].largo))})
                      </option>
                    </select>
                  </div>
                  <div className={styles.controlGroup}>
                    <label>% captación mercado:</label>
                    <select
                      value={porcentajeCaptacion}
                      onChange={(e) => setPorcentajeCaptacion(Number(e.target.value))}
                      className="form-select form-select-sm"
                    >
                      {ESCENARIOS_CAPTACION.map((p) => (
                        <option key={p} value={p}>
                          {p}%
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {(() => {
                  const oportunidad = calcularOportunidad();
                  if (!oportunidad) return null;

                  return (
                    <div className={styles.simuladorResultados}>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Mercado total mensual:</span>
                        <span className={styles.resultadoValor}>
                          {formatLocal(convertCLP(oportunidad.mercadoTotal))}
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Viajes a captar/mes:</span>
                        <span className={styles.resultadoValor}>{formatNumber(oportunidad.viajesCaptadosMes)}</span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Captación al {porcentajeCaptacion}%:</span>
                        <span className={styles.resultadoValorDestacado}>
                          {formatLocal(convertCLP(oportunidad.mercadoCaptadoMensual))}/mes
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Mercado captado anual:</span>
                        <span className={styles.resultadoValorDestacado}>
                          {formatLocal(convertCLP(oportunidad.mercadoCaptadoAnual))}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowOportunidadModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transporte;
